// ─────────────────────────────────────────────
//  Role & provisioning configuration
//  Single source of truth for who gets which role
//  when they first sign in. KEEP THIS IN SYNC with the
//  admin allowlist (adminEmails()) inside ../../firestore.rules.
//  The canonical allowlist is the adminEmails() literal in firestore.rules;
//  REACT_APP_ADMIN_EMAILS (this file) must resolve to the same set. Drift is
//  caught by scripts/check-admin-allowlist.mjs (npm run check:allowlist),
//  which gates `npm run deploy:rules`.
// ─────────────────────────────────────────────

// Emails that are provisioned as full admins (and AIC) on first sign-in.
// Everyone else gets the safe default role below and must be promoted by an
// admin from Setup → Users. Read from env so the list is not hard-baked into
// the shipped bundle; falls back to an empty list in production.
//
// MUST match the adminEmails() literal in firestore.rules — verified by
// scripts/check-admin-allowlist.mjs (npm run check:allowlist). See .env.example.
//
//   .env.local →  REACT_APP_ADMIN_EMAILS=gm@ah.com,jc@ah.com
import { testUserById } from "./testUsers";

const RAW_ADMIN_EMAILS = process.env.REACT_APP_ADMIN_EMAILS || "";

export const ADMIN_EMAILS = RAW_ADMIN_EMAILS
  .split(",")
  .map((e) => e.trim().toLowerCase())
  .filter(Boolean);

// Default facility a brand-new user is scoped to until an admin moves them.
export const DEFAULT_FACILITY_ID = process.env.REACT_APP_DEFAULT_FACILITY_ID || "default";
export const DEFAULT_FACILITY_NAME =
  process.env.REACT_APP_DEFAULT_FACILITY_NAME || "Main OR";

// Roles a user may self-assign on first sign-in. Anything else (admin, AIC)
// must be granted by an existing admin — enforced again in the security rules.
export const SELF_ASSIGNABLE_ROLES = ["provider", "observer"];

export const isAdminEmail = (email) =>
  !!email && ADMIN_EMAILS.includes(email.toLowerCase());

// ─────────────────────────────────────────────
//  ACCESS LEVELS — the single source of truth for the role hierarchy.
//
//  The four operational tiers, top to bottom:
//
//    Administrator       — sets up the app, facility, rooms, users, billing.
//                          Created on first install (email allowlist bootstrap).
//    Anesthesia In Charge— a doctor who oversees everything: opens/closes
//      (AIC)               rooms, assigns providers, approves breaks, and can
//                          also be assigned to a room.
//    Supervising Doctor  — oversees 1–4 rooms. Limited access: can change
//                          CRNA/provider assignments, but the AIC is notified
//                          of every change. Cannot open/close rooms, run setup,
//                          or approve breaks.
//    Clinical Staff      — CRNAs, AAs, Residents assigned to a room. Update
//                          their own room's status, request breaks, raise
//                          alerts, and follow the doctors' directions.
//
//  These tiers are derived from the stored `role` + `isAIC` fields — there is
//  NO separate stored "accessLevel" column, so existing user docs need no
//  migration. `accessLevelOf()` is the only place that mapping lives.
// ─────────────────────────────────────────────
export const ACCESS_LEVELS = {
  admin: {
    key: "admin",
    label: "Administrator",
    short: "Admin",
    color: "#f59e0b",
    icon: "🛠️",
    description:
      "Sets up the app, facility, rooms, users, and billing. The first account created on install.",
  },
  aic: {
    key: "aic",
    label: "Anesthesia In Charge (AIC)",
    short: "AIC",
    color: "#f59e0b",
    icon: "⭐",
    description:
      "Oversees everything — opens/closes rooms, assigns providers, approves breaks. Can also be assigned to a room.",
  },
  doctor: {
    key: "doctor",
    label: "Supervising Doctor",
    short: "Doctor",
    color: "#1e40af",
    icon: "🩺",
    description:
      "Oversees 1–4 rooms. Can change provider assignments, but the AIC is notified of every change.",
  },
  clinical_staff: {
    key: "clinical_staff",
    label: "Clinical Staff (CRNA · AA · Resident)",
    short: "Clinical Staff",
    color: "#7c3aed",
    icon: "👥",
    description:
      "Assigned to a room. Updates room status, requests breaks, raises alerts, follows the doctors' directions.",
  },
  boardmanager: {
    key: "boardmanager",
    label: "Board Manager (shared TV device)",
    short: "Board Mgr",
    color: "#0e7490",
    icon: "📺",
    description:
      "A shared per-facility device account that runs that facility's TV board with AIC-level board powers. Geofenced to the facility; assigns breaks/relief by picking from the day's roster (it is not a person).",
  },
  boardwall: {
    key: "boardwall",
    label: "Wall Display (read-only)",
    short: "Wall",
    color: "#475569",
    icon: "🖥️",
    description:
      "An unattended screen showing a facility's control panel. Reads the board and writes nothing, anywhere — enforced in firestore.rules, not by hiding buttons. Use this for a TV nobody stands at; use Board Manager for a device a person operates.",
  },
  scheduler: {
    key: "scheduler",
    label: "Scheduler",
    short: "Scheduler",
    color: "#0e7490",
    icon: "📅",
    description:
      "Inputs the OR schedule in the internal scheduling module. No access to the live board, setup, or clinical actions.",
  },
  observer: {
    key: "observer",
    label: "Observer",
    short: "Observer",
    color: "#555e7a",
    icon: "👁️",
    description: "Read-only view of the OR board.",
  },
};

// Map a stored profile ({ role, isAIC }) to its operational access level key.
// AIC is a designation layered on a doctor, so it is checked before the plain
// doctor role.
export function accessLevelOf({ role, isAIC } = {}) {
  if (role === "admin") return "admin";
  if (isAIC === true) return "aic";
  if (role === "anesthesiologist") return "doctor";
  if (role === "scheduler") return "scheduler";
  if (role === "boardmanager") return "boardmanager";
  if (role === "boardwall") return "boardwall";
  if (role === "observer") return "observer";
  // provider — and any unknown/legacy role — falls back to least-privilege.
  return "clinical_staff";
}

export const accessLevelMeta = (profile) =>
  ACCESS_LEVELS[accessLevelOf(profile)];

// ─────────────────────────────────────────────
//  TESTING MODE ("View as") — admin-only role preview.
//
//  Each option is a synthetic, NON-persisted profile patch that an admin can
//  step into to see exactly what a given tier sees. It never touches Firestore
//  and never grants real privileges; the security rules still evaluate the
//  admin's true identity, which is why the UI blocks writes while previewing.
// ─────────────────────────────────────────────
export const VIEW_AS_OPTIONS = [
  { key: "aic",            role: "anesthesiologist", isAIC: true,  subRole: null   },
  { key: "doctor",         role: "anesthesiologist", isAIC: false, subRole: null   },
  { key: "clinical_staff", role: "provider",         isAIC: false, subRole: "crna" },
  { key: "scheduler",      role: "scheduler",        isAIC: false, subRole: null   },
  { key: "boardmanager",   role: "boardmanager",     isAIC: false, subRole: null   },
  { key: "boardwall",      role: "boardwall",        isAIC: false, subRole: null   },
  { key: "observer",       role: "observer",         isAIC: false, subRole: null   },
];

// A key resolves to either a generic tier preview (VIEW_AS_OPTIONS) or a
// specific testing account (TEST_USERS). Test-account previews also patch
// uid + name so "my rooms"-style views show that account's assignments;
// they are only honoured while testing mode is ON (see AuthContext).
export const viewAsProfile = (key) => {
  const tier = VIEW_AS_OPTIONS.find((o) => o.key === key);
  if (tier) return tier;
  const tu = testUserById(key);
  if (tu) {
    return {
      key: tu.id,
      role: tu.role,
      isAIC: false,
      subRole: tu.subRole,
      uid: tu.id,
      name: tu.name,
      isTestAccount: true,
    };
  }
  return null;
};

// Build the Firestore profile for a user who has authenticated but has no
// profile document yet. Admins (allowlisted) are provisioned with elevated
// rights; everyone else gets the least-privilege default.
export function buildDefaultProfile(firebaseUser) {
  const email = firebaseUser.email || "";
  const admin = isAdminEmail(email);

  return {
    uid: firebaseUser.uid,
    name: firebaseUser.displayName || email.split("@")[0] || "New User",
    firstName: "",
    lastName: "",
    phone: "",
    address: "",
    email,
    role: admin ? "admin" : "provider",
    subRole: admin ? null : "crna",
    // NO facility on self-provision — unless the email is on the admin
    // allowlist. "default" is a real, LIVE facility, and facility membership is
    // the whole authorization model, so claiming it here let any authenticated
    // stranger read that board. A non-admin is provisioned with nothing and an
    // administrator grants membership from Setup → Users; isSafeSelfProfile()
    // in firestore.rules enforces the same constraint server-side.
    //
    // The allowlist branch is NOT a loophole: those emails are the canonical
    // admins (kept in sync with adminEmails() in firestore.rules by
    // scripts/check-admin-allowlist.mjs), the rules already let them
    // self-provision as admin, and this is the first-admin bootstrap path a
    // fresh project depends on. Stripping their facility would strand the
    // owner of a brand-new tenant outside their own board.
    facilityIds: admin ? [DEFAULT_FACILITY_ID] : [],
    activeFacilityId: admin ? DEFAULT_FACILITY_ID : null,
    activeFacilityName: admin ? DEFAULT_FACILITY_NAME : null,
    isAIC: false, // AIC is granted explicitly, never on self-provision
    status: "active",
    currentRoomId: null,
    currentRoomName: null,
    // Asked on first login ("Which facility do you work at most often?") —
    // drives provider-dropdown ordering app-wide.
    primaryFacilityId: null,
    fcmToken: null,
    packageTier: "basic",
  };
}

// The sandbox is a private, single-person facility that exists only inside the
// local store. The profile is an ADMIN one because the brief requires the user
// to create ORs, manage providers and explore every feature — which means
// /setup must open. It grants nothing outside the shim: there is no other
// tenant there, and no security rules, because there is no server.
export const SANDBOX_FACILITY_ID = "sbx";
export const SANDBOX_FACILITY_NAME = "My Test Facility";

export function buildSandboxProfile(firebaseUser) {
  const email = firebaseUser.email || "";
  return {
    ...buildDefaultProfile(firebaseUser),
    role: "admin",
    subRole: null,
    facilityIds: [SANDBOX_FACILITY_ID],
    activeFacilityId: SANDBOX_FACILITY_ID,
    activeFacilityName: SANDBOX_FACILITY_NAME,
    sandbox: true,
    onboarded: false,
    email,
  };
}
